import { EnvironmentInjector, Injectable, inject } from '@angular/core';
import { fromJson, toJson } from '@bufbuild/protobuf';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import {
  CollectionNames,
  UserServiceCreateResponseJson,
  UserServiceCreateResponseSchema,
  UserServiceListRequestSchema,
  defaultApiSerializeJsonOptions,
} from '@vmspdb/definitions';
import { GlobalErrorHandlerService } from '@vmspdb/ui/common';
import { AuthRunEffects } from '@vmspdb/ui/store';
import { EMPTY, Observable, filter, of } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';
import { UserRepositoryService, withSearchSync } from '../../repositories';
import { VOID } from '../../shared/utils';
import * as UsersActions from './users.actions';

@Injectable()
export class UsersEffects extends AuthRunEffects {
  private readonly repository = inject(UserRepositoryService);
  private readonly injector = inject(EnvironmentInjector);
  private readonly errorHandler = inject(GlobalErrorHandlerService);

  listUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.listUsers),
      concatLatestFrom(() => of(true)), //this.store.select(selectAuthLoading)),
      filter(([, loading]) => loading), // see https://github.com/eslint/eslint/issues/7838#issuecomment-269982977
      withSearchSync(CollectionNames.Users, this.injector),
      switchMap(([payload]) =>
        this.repository
          .list(
            fromJson(UserServiceListRequestSchema, {
              pageSize: 5000,
              ...payload.params,
            }),
          )
          .pipe(
            map((result) =>
              UsersActions.loadUsers({
                users: (result.items ?? []).map(
                  (item) =>
                    toJson(
                      UserServiceCreateResponseSchema,
                      item,
                      defaultApiSerializeJsonOptions,
                    ) as UserServiceCreateResponseJson,
                ),
              }),
            ),
            catchError((error) => {
              this.errorHandler.handleError(error);
              return of(UsersActions.listUsersFailure(error));
            }),
          ),
      ),
    );
  });

  loadUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.loadUsers),
      /** An EMPTY observable only emits completion. Replace with your own observable API request */
      concatMap(() => EMPTY as Observable<{ type: string }>),
    );
  });

  deleteUser$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(UsersActions.deleteUser),
        /** An EMPTY observable only emits completion. Replace with your own observable API request */
        switchMap(({ id }) => this.repository.delete(id)),
        catchError((error) => {
          this.errorHandler.handleError(error);
          return VOID;
        }),
      );
    },
    { dispatch: false },
  );

  constructor() {
    super(inject(Actions));
  }
}
